// Client-side condition evaluator for Form Flow branching (Batch 2b-i).
//
// Mirrors backend/services/condition_engine.py operators + coercion so the
// JSON shape stays canonical, but runs in the browser — a flow picks its next
// screen live, with no per-question server round-trip. Field references use the
// `answer.<question_id>` convention (see buildAnswerContext).

const toStr = (v) => (v === null || v === undefined ? '' : String(v).toLowerCase().trim());
const toNum = (v) => { const n = Number(v); return Number.isNaN(n) ? 0 : n; };
const toList = (v) => Array.isArray(v) ? v : (typeof v === 'string' ? v.split(',').map(s => s.trim()) : []);
const isEmpty = (a) => a === null || a === undefined || a === '' || (Array.isArray(a) && a.length === 0);
const truthy = (a) => a === true || a === 1 || ['true', 'yes', '1', 'y'].includes(toStr(a));

function evalCondition(cond, ctx) {
    const actual = ctx[cond.field];
    const value = cond.value;
    switch (cond.operator) {
        case 'equals': return toStr(actual) === toStr(value);
        case 'not_equals': return toStr(actual) !== toStr(value);
        case 'contains':
            return Array.isArray(actual)
                ? actual.some(el => toStr(el).includes(toStr(value)))
                : toStr(actual).includes(toStr(value));
        case 'not_contains':
            return !(Array.isArray(actual)
                ? actual.some(el => toStr(el).includes(toStr(value)))
                : toStr(actual).includes(toStr(value)));
        case 'starts_with': return toStr(actual).startsWith(toStr(value));
        case 'ends_with': return toStr(actual).endsWith(toStr(value));
        case 'in_list': return toList(value).map(toStr).includes(toStr(actual));
        case 'not_in_list': return !toList(value).map(toStr).includes(toStr(actual));
        case 'greater_than': return toNum(actual) > toNum(value);
        case 'less_than': return toNum(actual) < toNum(value);
        case 'greater_equal': return toNum(actual) >= toNum(value);
        case 'less_equal': return toNum(actual) <= toNum(value);
        case 'is_empty': return isEmpty(actual);
        case 'is_not_empty': return !isEmpty(actual);
        case 'is_true': return truthy(actual);
        case 'is_false': return !truthy(actual);
        case 'exists': return actual !== undefined && actual !== null;
        case 'not_exists': return actual === undefined || actual === null;
        default:
            console.warn('[flowConditions] unknown operator:', cond.operator);
            return false;
    }
}

// Port of evaluate_condition_group. Empty group → true (matches Python; a rule
// with no conditions acts as a catch-all).
export function evaluateGroup(group, ctx) {
    if (!group) return true;
    const logic = group.logic || 'and';
    const results = [
        ...(group.conditions || []).map(c => evalCondition(c, ctx)),
        ...(group.groups || []).map(g => evaluateGroup(g, ctx)),
    ];
    if (results.length === 0) return true;
    return logic === 'or' ? results.some(Boolean) : results.every(Boolean);
}

// { question_id: value } → { 'answer.<question_id>': value }
export function buildAnswerContext(answers) {
    const ctx = {};
    Object.keys(answers || {}).forEach(k => { ctx[`answer.${k}`] = answers[k]; });
    return ctx;
}

export const FLOW_END = '__end__';

// Decide the screen index to show after `currentIndex`, given answers-so-far.
// Returns a numeric index or FLOW_END. Resolves a step's branch rules in order
// (first match → go_to), else default_go_to, else the linear next screen.
// go_to references a step_id; an unknown/stale target falls back to linear next.
export function nextIndex(screens, currentIndex, answers) {
    const ctx = buildAnswerContext(answers);
    const step = screens[currentIndex];
    const branch = step && step.branch;
    let target; // step_id | FLOW_END | undefined

    if (branch && Array.isArray(branch.rules)) {
        for (const rule of branch.rules) {
            if (evaluateGroup(rule.condition_group || {}, ctx)) { target = rule.go_to; break; }
        }
    }
    if (target === undefined) target = branch ? branch.default_go_to : undefined;

    const linearNext = currentIndex + 1 >= screens.length ? FLOW_END : currentIndex + 1;
    if (target === FLOW_END) return FLOW_END;
    if (target === undefined || target === null || target === '') return linearNext;

    const idx = screens.findIndex(s => s.step_id === target);
    return idx === -1 ? linearNext : idx;   // stale/unknown target → linear next
}
